import { useEffect, useState } from "react";

const STORAGE_KEY = "lh2o_pwa_installed";

function readPersistedInstall() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch (_) {
    return false;
  }
}

function detectStandalone() {
  if (typeof window === "undefined") return false;
  // iOS Safari uses navigator.standalone; modern browsers expose
  // (display-mode: standalone) media query — and on Android, a launched
  // PWA also reports (display-mode: minimal-ui) on some skins.
  if (window.navigator && window.navigator.standalone === true) return true;
  if (window.matchMedia) {
    if (window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.matchMedia("(display-mode: minimal-ui)").matches) return true;
    if (window.matchMedia("(display-mode: window-controls-overlay)").matches)
      return true;
  }
  return false;
}

/**
 * Returns true once we've ever detected the LocalH2O PWA running in
 * installed (standalone) mode on this origin. Detection is "sticky":
 *
 *  - On every load we check `display-mode: standalone` + `navigator.standalone`.
 *  - If the page is currently running standalone, we set localStorage so that
 *    subsequent visits (even from the regular browser tab) remember the user
 *    has the app installed.
 *  - We also subscribe to the `appinstalled` event (fires on Android / Desktop
 *    Chrome the moment the user accepts the install dialog) and to live
 *    `display-mode` changes so the UI updates without a reload.
 */
export function useInstalledState() {
  const [installed, setInstalled] = useState(
    () => detectStandalone() || readPersistedInstall(),
  );

  useEffect(() => {
    const persist = () => {
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) { /* ignore */ }
    };

    if (detectStandalone()) {
      persist();
      setInstalled(true);
    }

    const onInstalled = () => {
      persist();
      setInstalled(true);
    };
    window.addEventListener("appinstalled", onInstalled);

    const mqls = [];
    if (window.matchMedia) {
      ["(display-mode: standalone)", "(display-mode: minimal-ui)", "(display-mode: window-controls-overlay)"].forEach((q) => {
        const mql = window.matchMedia(q);
        const handler = (ev) => {
          if (ev.matches) {
            persist();
            setInstalled(true);
          }
        };
        if (mql.addEventListener) {
          mql.addEventListener("change", handler);
        } else if (mql.addListener) {
          mql.addListener(handler);
        }
        mqls.push({ mql, handler });
      });
    }

    return () => {
      window.removeEventListener("appinstalled", onInstalled);
      mqls.forEach(({ mql, handler }) => {
        if (mql.removeEventListener) mql.removeEventListener("change", handler);
        else if (mql.removeListener) mql.removeListener(handler);
      });
    };
  }, []);

  return installed;
}
